import { defineStore } from 'pinia';
import { isNumericValue } from '~~/helpers';
import { useNuxtApp, useRuntimeConfig, useRouter } from 'nuxt/app';

import { PromiseReplicate } from '~~/helpers/replicate';
import type { IEstacion, IServicio, IServicioResponse } from '~~/interfaces';
import {
  ALLOWED_SERVICES,
  ALLOWED_SERVICES_PAIRS,
  BASE_SERVICIO_ID,
  BASE_SERVICIO_SLUG,
  type TServiciosId,
  type TServiciosSlug,
  ESTACIONES_BASE_SERVICIOS,
  ESTACION_BASE_BIOTREN_ID,
  SERVICIOS_FERIADOS_OUT,
} from '~~/constants';
import { computed, ref } from 'vue';

const version = '';

export const useServiciosStore = defineStore('servicios', () => {
  const { $db } = useNuxtApp();
  const {
    public: { dbUrl: BASE_URL = 'https://db.mibiotren.cl' },
  } = useRuntimeConfig();
  const router = useRouter();
  const isOnline = useOnline();

  let Servicios: null | PouchDB.Database<IServicio> = null;
  let ServiciosRemote: null | PouchDB.Database<IServicio> = null;

  const servicio = computed({
    get: () =>
      String(router.currentRoute.value.params?.servicio || BASE_SERVICIO_SLUG),
    set: (newSrv) => {
      if (!servicios.value.has(newSrv)) return;

      return router.push({
        params: {
          servicio: newSrv,
        },
      });
    },
  });

  const servicioId = computed({
    get: () => ALLOWED_SERVICES_PAIRS.get(servicio.value as TServiciosSlug),
    set: (newSrv) => {
      if (servicio.value === newSrv) return;
      servicio.value = String(newSrv);
    },
  });
  const serviciosArray = ref<IServicio[]>([]);
  const initLoad = ref(0);
  const isFeriado = ref(false);

  const servicios = computed<Map<string, IServicio>>(() => {
    return new Map<string, IServicio>(
      serviciosArray.value
        .filter((srv) =>
          (ALLOWED_SERVICES as unknown as string[]).includes(
            srv.srvid as TServiciosId
          )
        )
        .map((srv) => [srv.slug, srv])
    );
  });

  const serviciosPorId = computed(() => {
    return new Map<string, IServicio>(
      serviciosArray.value
        .filter((srv) =>
          (ALLOWED_SERVICES as unknown as string[]).includes(
            srv.srvid as TServiciosId
          )
        )
        .map((srv) => [srv.srvid, srv])
    );
  });

  const servicioActivo = computed<IServicio | undefined>(() => {
    return servicios.value.get(String(servicio.value));
  });

  const estacionesBase = computed<IEstacion[]>(() => {
    const ests = servicioActivo.value?.estaciones || [];
    return ests;
  });

  const estaciones = computed<Map<string, IEstacion>>(() => {
    return estacionesBase.value?.reduce((init, estacion) => {
      init.set(String(estacion._id), estacion);
      init.set(String(estacion.slug), estacion);
      return init;
    }, new Map());
  });

  const estacionBaseSalidaLlegada = computed<{
    salida: IEstacion;
    llegada: IEstacion;
  }>(() => {
    if (!servicioActivo.value)
      return {
        salida: estacionesBase.value[0]!,
        llegada: estacionesBase.value.slice(-1)[0]!,
      };

    const servicioId = servicioActivo.value?.srvid || BASE_SERVICIO_ID;
    const servicioSlug = servicioActivo.value?.slug as TServiciosSlug;

    const [salidaParam, llegadaParam] =
      router.currentRoute.value.params.estaciones || [];

    const salidaIdParam = salidaParam
      ? estaciones.value.get(salidaParam)
      : undefined;
    const llegadaIdParam = llegadaParam
      ? estaciones.value.get(llegadaParam)
      : undefined;

    const estacionSalidaId =
      salidaIdParam?._id ||
      (servicioId === BASE_SERVICIO_ID ? ESTACION_BASE_BIOTREN_ID : '1');

    const estacionFinalId =
      llegadaIdParam?._id || ESTACIONES_BASE_SERVICIOS[servicioSlug];

    const estacion1 =
      estaciones.value.get(estacionSalidaId) || estacionesBase.value[0]!;
    const estacion2 =
      estaciones.value.get(estacionFinalId) ||
      estacionesBase.value.slice(-1)[0]!;

    return {
      salida: estacion1,
      llegada: estacion2,
    };
  });

  function initDB() {
    if (!$db) return;

    if (!Servicios) {
      Servicios = $db.Servicios;
    }
    if (!ServiciosRemote) {
      ServiciosRemote = $db.ServiciosRemote;
    }
  }

  function getServicioApi(
    idServicio: string,
    all?: false | undefined
  ): Promise<IServicio>;
  function getServicioApi(idServicio: string, all: true): Promise<IServicio[]>;

  function getServicioApi(
    idServicio: string,
    all?: boolean
  ): Promise<IServicio[] | IServicio> {
    const URL = `${BASE_URL}/servicios${version || ''}/${all ? '_all_docs?include_docs=true' : idServicio}`;

    return $fetch<IServicio | IServicioResponse>(URL).then((d) => {
      if ('rows' in d && typeof all === 'boolean') {
        return d.rows.map((doc) => doc.doc) as IServicio[];
      }
      return d as IServicio;
    });
  }

  async function getServicioPouch(idServicio: string) {
    if (!Servicios) throw new Error('Servicios no iniciados');
    return Servicios.get(idServicio);
  }

  async function getServicio({
    idServicio = BASE_SERVICIO_ID,
    external = false,
  }: { idServicio?: string; external?: boolean } = {}) {
    let S: IServicio | null;

    if (!idServicio) throw new Error('Servicio no definido');
    if (idServicio === servicioId.value) {
      return servicio.value;
    }

    servicioId.value = idServicio as TServiciosId;

    if ((!external || (!isOnline && external)) && Servicios) {
      S = await getServicioPouch(idServicio).catch((e) => {
        if (e.message === 'missing') {
          return getServicioApi(idServicio);
        }
        return null;
      });
    } else {
      S = await getServicioApi(idServicio).catch((e) => {
        console.log(e, 'ERROR');
        return null;
      });
    }

    if (!S) {
      return S;
    }

    const indexServicio = serviciosArray.value.findIndex(
      (s) => String(s.srvid) === String(S?.srvid)
    );

    if (indexServicio < 0) {
      serviciosArray.value.push(S);
    } else {
      serviciosArray.value[indexServicio] = S;
    }

    return S;
  }

  function updateFn() {
    // initLoad.value = percent;
  }

  async function syncServicios() {
    initDB();

    if (!Servicios || !ServiciosRemote)
      throw new Error('Sin servicios iniciados');

    const { docs_written: docs } = (await PromiseReplicate(
      Servicios,
      ServiciosRemote,
      updateFn
    )) as PouchDB.Replication.ReplicationResult<object>;

    if (docs) {
      await Servicios.compact().catch((e) => console.log(e, 'Compact err'));
      return Promise.resolve(Servicios.allDocs({ include_docs: true }));
    }
    return Promise.reject(Servicios.allDocs({ include_docs: true }));
  }

  const parseResult = (
    result: PouchDB.Query.Response<IServicio> | undefined
  ): IServicio[] | undefined =>
    result?.rows.map((r) => r.doc).filter(Boolean) as IServicio[];

  async function populateServicios(remote = false) {
    if (!ServiciosRemote && !remote) {
      initDB();
      if (!ServiciosRemote) throw new Error('Servicios no inicializados');
    }

    if (serviciosArray.value.length > 0) {
      return serviciosArray.value;
    }

    let servsList: IServicio[] | undefined;

    if (remote) {
      servsList = await $fetch<IServicioResponse>(
        `${BASE_URL}/servicios/_all_docs?include_docs=true`
      ).then(parseResult);
    } else {
      if (!Servicios) throw new Error('Servicios locales no cargados');

      servsList = await Servicios.allDocs({
        include_docs: true,
        attachments: true,
      })
        .catch(() =>
          ServiciosRemote?.allDocs({ include_docs: true, attachments: true })
        )
        .then(parseResult);
    }

    if (!servsList?.length) {
      serviciosArray.value ||= [];
    } else {
      serviciosArray.value = servsList
        .map((ser) => {
          if (ser) return ser;
        })
        .reduce((init, d) => {
          const existeSrv = init.findIndex((srv) => srv.srvid === d?.srvid);
          if (
            d?.srvid &&
            existeSrv < 0 &&
            (ALLOWED_SERVICES as unknown as string[]).includes(
              d?.srvid as TServiciosId
            )
          ) {
            return [...init, d];
          }
          return init;
        }, [] as IServicio[]);
    }

    return serviciosArray.value;
  }

  async function checkFeriado() {
    let today = new Date().toISOString().split('T')[0]!;

    if ('toLocaleDateString' in new Date()) {
      today = new Date().toLocaleDateString('sv-SE');
    }

    await $fetch<
      { _id: string; _rev: string } | { error: string; reason: string }
    >(`${BASE_URL}/feriados/${today}`)
      .then((d) => {
        if ('_id' in d && d._id === today) {
          isFeriado.value = true;
        }
      })
      .catch(() => {
        isFeriado.value = false;
      });
  }

  function cambiarServicio({
    servicio: srv = BASE_SERVICIO_ID,
  }: {
    servicio?: string;
  }) {
    if (
      servicioActivo.value?.slug === srv ||
      servicioActivo.value?._id === srv
    ) {
      return;
    }

    const newSrv = servicios.value.get(srv);
    if (!newSrv) return;

    servicioId.value = newSrv?.srvid as TServiciosId;
  }

  function existeServicio(
    slug: TServiciosSlug = BASE_SERVICIO_SLUG
  ): IServicio | undefined {
    return servicios.value?.get(slug);
  }

  return {
    // state
    servicio,
    servicioId,
    serviciosArray,
    initLoad,

    initDB,
    getServicioApi,
    getServicioPouch,
    getServicio,
    updateFn,
    syncServicios,
    populateServicios,
    cambiarServicio,
    existeServicio,
    findEstacionInServicio: (
      estacionIdOrSlug: string,
      servicioIdOrSlug: string = servicioActivo.value?.srvid || BASE_SERVICIO_ID
    ) => {
      const servicio = servicios.value.get(servicioIdOrSlug);
      if (!servicio) return;
      const isSlugOrIdEstacion = !isNumericValue(estacionIdOrSlug)
        ? 'slug'
        : '_id';

      const estacion = servicio.estaciones.find((e) => {
        return e[isSlugOrIdEstacion] === String(estacionIdOrSlug);
      });
      return estacion;
    },
    checkFeriado,
    isFeriado: computed(
      () =>
        isFeriado.value &&
        SERVICIOS_FERIADOS_OUT.includes(servicioId.value || BASE_SERVICIO_ID)
    ),

    // getters
    servicios,
    serviciosPorId,
    servicioActivo,
    estaciones,
    estacionesBase,
    estacionBaseSalidaLlegada,
  };
});

export type TServiciosStore = typeof useServiciosStore;
